import React, { useState, useCallback, useEffect } from 'react';
import './PlaceSearchAutocomplete.css';
import Location from '@/models/location';
import { CityFormData } from '@/types/city';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import CloseButton from '../Buttons/CloseButton/CloseButton';
import DaySelectionModal from './DaySelectionModal';
import PlacePicker, { type PlacePickerTab } from '@/components/PlacePicker/PlacePicker';
import type {
    ItineraryImportDraft,
    ItineraryImportSourceType,
} from '@/models/itineraryImportTypes';
import { debug, log } from '@/utils/logger';

/**
 * Props for PlaceSearchAutocomplete component
 */
interface PlaceSearchAutocompleteProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when modal should be closed */
    onClose: () => void;
    /** Callback when a place is selected and should be added */
    onAddPlace: (location: Location, dayIndex: number) => void;
    /** Current day index to add places to (for single-day trips) */
    dayIndex: number;
    /** City data for location biasing */
    city: CityFormData | null | undefined;
    /** Only show Explore results in this country (a list scoped to a whole country). */
    country?: string | null;
    /** Array of day objects for multi-day trips */
    days?: Array<{ dayIndex: number; date: string }>;
    /** Class name for additional styling */
    className?: string;
    /** Post type context: 'List' or 'Itinerary' (for contextual messaging) */
    postType?: 'List' | 'Itinerary';
    /** Callback when add user place link is clicked */
    onCustomCardClick?: () => void;
    /** Label for the first tab (default "Search"; lists often use "Explore") */
    searchTabLabel?: string;
    /** Show Copy & Paste tab (parse text → multi-select). Requires onAddPlacesBatch. */
    showCopyPasteTab?: boolean;
    /** Show Social tab (Instagram/TikTok link → multi-select). Requires onAddPlacesBatch. */
    showSocialTab?: boolean;
    /** Add multiple places at once (Copy & Paste / Social confirm); modal closes after this runs */
    onAddPlacesBatch?: (locations: Location[]) => void | Promise<void>;
    /** Social tab (v2 import draft): append selected rows onto the open trip/list. */
    onSocialImportDraftConfirm?: (params: {
        draft: ItineraryImportDraft;
        selectedClientIds: Set<string>;
        importId: string;
        importStartedAt: string;
        sourceType: ItineraryImportSourceType;
    }) => void | Promise<void>;
    /** Open with a specific tab pre-selected (e.g. 'copy-paste' for the paste-text entry flow). Default: PlacePicker's own default ('saves'). */
    initialTab?: PlacePickerTab;
    /**
     * Lookup replaces a custom stop or import row. Search only, no day picker,
     * and the typed query is not saved unless the user picks a result.
     */
    variant?: 'add' | 'lookup';
    /** Sheet title. Lookup defaults to "Find this place". */
    sheetTitle?: string;
    /** Seeds the search box in lookup mode. */
    initialSearchQuery?: string;
}

/**
 * PlaceSearchAutocomplete component – modal wrapper around PlacePicker.
 *
 * Keeps existing props / call-sites unchanged. Delegates all search, My Trips,
 * and Saves UI to the shared PlacePicker component. Handles multi-day trip
 * day-selection after a place is picked.
 */
const PlaceSearchAutocomplete: React.FC<PlaceSearchAutocompleteProps> = ({
    isOpen,
    onClose,
    onAddPlace,
    dayIndex,
    city,
    country,
    days = [],
    className = '',
    postType = 'Itinerary',
    onCustomCardClick,
    searchTabLabel = 'Search',
    showCopyPasteTab = false,
    showSocialTab = false,
    onAddPlacesBatch,
    onSocialImportDraftConfirm,
    initialTab,
    variant = 'add',
    sheetTitle,
    initialSearchQuery = '',
}) => {
    const [showDaySelection, setShowDaySelection] = useState(false);
    const [pendingLocation, setPendingLocation] = useState<Location | null>(null);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setShowDaySelection(false);
            setPendingLocation(null);
        }
    }, [isOpen]);

    const handlePlacePickerSelect = useCallback(
        (location: Location): void => {
            debug('[PlaceSearchAutocomplete] Place selected from PlacePicker', {
                locationName: location.name,
                locationId: location._id,
                dayIndex,
                isMultiDay: days.length > 1,
            });

            if (variant !== 'lookup' && days.length > 1) {
                debug('[PlaceSearchAutocomplete] Multi-day trip, showing day selection');
                setPendingLocation(location);
                setShowDaySelection(true);
            } else {
                onAddPlace(location, dayIndex);
                onClose();
            }
        },
        [onAddPlace, onClose, dayIndex, days, variant]
    );

    const handleDaySelect = useCallback(
        (selectedDayIndex: number): void => {
            if (pendingLocation) {
                onAddPlace(pendingLocation, selectedDayIndex);
                setPendingLocation(null);
                setShowDaySelection(false);
                onClose();
            }
        },
        [onAddPlace, onClose, pendingLocation]
    );

    const handleDaySelectionClose = useCallback((): void => {
        setShowDaySelection(false);
        setPendingLocation(null);
    }, []);

    const handleCustomCardClick = useCallback((): void => {
        debug('[PlaceSearchAutocomplete] Add user place link clicked', { dayIndex, postType });
        if (onCustomCardClick) {
            onCustomCardClick();
        }
    }, [onCustomCardClick, dayIndex, postType]);

    const handleCopyPasteConfirm = useCallback(
        async (locations: Location[]): Promise<void> => {
            if (locations.length === 0) return;
            log('[PlaceSearchAutocomplete] handleCopyPasteConfirm START', { count: locations.length });
            try {
                await Promise.resolve(onAddPlacesBatch?.(locations));
                log('[PlaceSearchAutocomplete] handleCopyPasteConfirm onAddPlacesBatch resolved, calling onClose');
                onClose();
            } catch (err) {
                log('[PlaceSearchAutocomplete] handleCopyPasteConfirm onAddPlacesBatch THREW', { err: String(err) });
                throw err;
            }
        },
        [onAddPlacesBatch, onClose]
    );

    const handleSocialImportDraftConfirm = useCallback(
        async (params: {
            draft: ItineraryImportDraft;
            selectedClientIds: Set<string>;
            importId: string;
            importStartedAt: string;
            sourceType: ItineraryImportSourceType;
        }): Promise<void> => {
            try {
                await Promise.resolve(onSocialImportDraftConfirm?.(params));
                onClose();
            } catch (err) {
                throw err;
            }
        },
        [onSocialImportDraftConfirm, onClose],
    );

    const copyPasteEnabled = Boolean(showCopyPasteTab && onAddPlacesBatch);
    const socialTabEnabled = Boolean(
        showSocialTab && (onAddPlacesBatch || onSocialImportDraftConfirm),
    );
    // Batch add is available whenever the parent can accept several places. It used to be derived
    // from the Copy & Paste / Social tabs, which meant hiding those tabs silently downgraded the
    // Explore tab to single-tap add. Behaviour is unchanged for today's callers.
    const batchConfirmEnabled = Boolean(onAddPlacesBatch);

    if (!isOpen) return null;

    const contextLabel = postType === 'List' ? 'list' : 'itinerary';
    const isLookup = variant === 'lookup';
    const title = sheetTitle ?? (isLookup ? 'Find this place' : 'Add places');

    return (
        <ResponsiveOverlay
            onClose={onClose}
            presentation="sheet"
            unwrapped
            showSheetHandle={false}
            aria-label={title}
        >
            <div className={`place-search-autocomplete mobile-sheet ${className}`}>
                <div className="place-search-autocomplete-drag-handle" aria-hidden="true">
                    <div className="place-search-autocomplete-drag-pill" />
                </div>
                <div className="place-search-autocomplete-header">
                    <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
                        <h2 className="place-search-autocomplete-title m-0 min-w-0 flex-1 truncate">
                            {title}
                        </h2>
                    </div>
                    <CloseButton
                        onClick={onClose}
                        ariaLabel="Close place search"
                        size={24}
                    />
                </div>

                <div className="place-search-autocomplete-content gt-keyboard-scroll">
                    <PlacePicker
                        city={city}
                        country={country}
                        onSelectPlace={handlePlacePickerSelect}
                        contextLabel={contextLabel}
                        onCustomCardClick={
                            !isLookup && onCustomCardClick ? handleCustomCardClick : undefined
                        }
                        searchTabLabel={searchTabLabel}
                        showCopyPasteTab={!isLookup && copyPasteEnabled}
                        showSocialTab={!isLookup && socialTabEnabled}
                        onCopyPasteConfirm={
                            !isLookup && batchConfirmEnabled ? handleCopyPasteConfirm : undefined
                        }
                        onSocialImportDraftConfirm={
                            !isLookup && onSocialImportDraftConfirm
                                ? handleSocialImportDraftConfirm
                                : undefined
                        }
                        exploreUsesBatchSelect={!isLookup && batchConfirmEnabled}
                        initialTab={isLookup ? 'search' : initialTab}
                        searchOnly={isLookup}
                        initialSearchQuery={isLookup ? initialSearchQuery : undefined}
                    />
                </div>
            </div>

            {/* Day Selection Modal (for multi-day trips) */}
            {showDaySelection && pendingLocation && (
                <DaySelectionModal
                    isOpen={showDaySelection}
                    onClose={handleDaySelectionClose}
                    onSelectDay={handleDaySelect}
                    days={days.length > 0 ? days : [{ dayIndex, date: `Day ${dayIndex + 1}` }]}
                    placeName={pendingLocation.name}
                />
            )}
        </ResponsiveOverlay>
    );
};

export default PlaceSearchAutocomplete;
